import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { GALLERY } from "../mock";
import { videoStarted, videoStopped } from "../lib/mediaBus";

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const imgs = GALLERY.images;
  const video = GALLERY.video;

  useEffect(() => {
    if (!playing) return;
    videoStarted();
    return () => videoStopped();
  }, [playing]);

  const close = useCallback(() => setActive(null), []);
  const next = useCallback(
    (e) => {
      e && e.stopPropagation();
      setActive((i) => (i + 1) % imgs.length);
    },
    [imgs.length]
  );
  const prev = useCallback(
    (e) => {
      e && e.stopPropagation();
      setActive((i) => (i - 1 + imgs.length) % imgs.length);
    },
    [imgs.length]
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, close, next, prev]);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#16110E] text-[#F3EAD7]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto reveal">
          <span className="label-mono text-[#F0C766]">{GALLERY.eyebrow}</span>
          <h2 className="font-display font-black text-5xl md:text-7xl mt-4">
            {GALLERY.title}
          </h2>
          <p className="mt-5 text-[#B7AC97] leading-relaxed">{GALLERY.blurb}</p>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {/* Featured click-to-play video (YouTube Short) */}
          <div className="reveal relative overflow-hidden rounded-xl md:row-span-2 md:col-span-2 bg-black">
            {playing ? (
              <>
                <iframe
                  className="w-full h-72 md:h-full"
                  src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
                  title={video.label}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
                <button
                  onClick={() => setPlaying(false)}
                  className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/70 hover:bg-[#D5521B] text-[#F3EAD7] flex items-center justify-center transition-colors"
                  aria-label="Stop video"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setPlaying(true)}
                className="group w-full h-72 md:h-full relative focus:outline-none focus:ring-2 focus:ring-[#D5521B]"
                aria-label={`Play video: ${video.label}`}
              >
                <img
                  src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                  alt={video.label}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#D5521B] flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(213,82,27,0.7)] transition-transform duration-300 group-hover:scale-110">
                    <Play size={30} className="text-[#FBF6EA] ml-1" fill="currentColor" />
                  </span>
                </span>
                <span className="absolute bottom-4 left-4 right-4 text-left">
                  <span className="block label-mono text-[#F0C766]">Watch</span>
                  <span className="block font-display text-xl md:text-2xl font-bold text-[#F3EAD7] mt-1">
                    {video.label}
                  </span>
                </span>
              </button>
            )}
          </div>

          {imgs.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="reveal group relative overflow-hidden rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F0C766]"
              style={{ transitionDelay: `${(i % 4) * 0.06}s` }}
            >
              <img
                src={img.src}
                alt={img.label}
                className="img-zoom w-full object-cover h-40 md:h-48"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end p-4">
                <span className="label-mono text-[#F3EAD7]">{img.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          onClick={close}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
        >
          <button
            onClick={close}
            className="absolute top-5 right-5 text-[#F3EAD7] hover:text-[#F0C766] transition-colors"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <button
            onClick={prev}
            className="absolute left-3 md:left-8 text-[#F3EAD7] hover:text-[#F0C766] transition-colors p-2"
            aria-label="Previous"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={next}
            className="absolute right-3 md:right-8 text-[#F3EAD7] hover:text-[#F0C766] transition-colors p-2"
            aria-label="Next"
          >
            <ChevronRight size={40} />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-4xl w-full"
          >
            <img
              src={imgs[active].src}
              alt={imgs[active].label}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-center font-display italic text-xl text-[#F3EAD7] mt-4">
              {imgs[active].label}
              <span className="label-mono text-[#8A7C68] ml-3">
                {active + 1} / {imgs.length}
              </span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
